import { useState } from "react";
import { useAuth } from "../auth/AuthContext";
import { useT } from "../i18n";
import { useTheme, THEME_PREFS } from "../theme";
import { LOCALES } from "../i18n";
import type { Locale, ThemePref } from "../api/types";
import { api } from "../api/client";
import { Select } from "../components/Select";

export function ProfilePage() {
  const { user, setUser } = useAuth();
  const { t, locale, setLocale } = useT();
  const { pref, setPref } = useTheme();

  const [displayName, setDisplayName] = useState(user?.displayName ?? "");
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileMsg, setProfileMsg] = useState<string | null>(null);

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [savingPassword, setSavingPassword] = useState(false);
  const [passwordMsg, setPasswordMsg] = useState<{ ok: boolean; text: string } | null>(null);

  if (!user) return null;

  async function saveProfile() {
    if (!user) return;
    setSavingProfile(true);
    setProfileMsg(null);
    try {
      const updated = await api.updateProfile({ displayName });
      setUser(updated);
      setProfileMsg(t("profile.profileSaved"));
    } catch (err) {
      setProfileMsg((err as Error).message);
    } finally {
      setSavingProfile(false);
    }
  }

  async function changePassword(e: React.FormEvent) {
    e.preventDefault();
    setPasswordMsg(null);
    if (newPassword.length < 6) {
      setPasswordMsg({ ok: false, text: t("profile.passwordTooShort") });
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordMsg({ ok: false, text: t("profile.passwordMismatch") });
      return;
    }
    setSavingPassword(true);
    try {
      await api.changePassword(currentPassword, newPassword);
      setPasswordMsg({ ok: true, text: t("profile.passwordChanged") });
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err) {
      setPasswordMsg({ ok: false, text: (err as Error).message });
    } finally {
      setSavingPassword(false);
    }
  }

  async function onLocaleChange(l: Locale) {
    setLocale(l);
    if (user) {
      try {
        const updated = await api.updateProfile({ locale: l });
        setUser(updated);
      } catch {
        /* best-effort; local state already updated */
      }
    }
  }

  async function onThemeChange(p: ThemePref) {
    setPref(p);
    if (user) {
      try {
        const updated = await api.updateProfile({ theme: p });
        setUser(updated);
      } catch {
        /* best-effort */
      }
    }
  }

  return (
    <div className="px-4 py-4 max-w-2xl mx-auto space-y-8">
      <h1 className="text-xl font-semibold">{t("profile.title")}</h1>

      {/* Account info + display name */}
      <section className="p-5 rounded-xl bg-brand-surface border border-brand-line">
        <h2 className="text-sm font-semibold mb-4">{t("profile.account")}</h2>
        <label className="block mb-4">
          <span className="text-sm text-brand-muted">{t("profile.email")}</span>
          <input
            value={user.email}
            disabled
            className="mt-1 w-full px-3 py-2 rounded bg-brand-dark border border-brand-line text-brand-muted"
          />
        </label>
        <label className="block mb-4">
          <span className="text-sm text-brand-muted">{t("profile.displayName")}</span>
          <input
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder={t("profile.displayNamePlaceholder")}
            className="mt-1 w-full px-3 py-2 rounded bg-brand-dark border border-brand-line focus:border-brand-red outline-none"
          />
        </label>
        <div className="flex items-center gap-3">
          <button
            onClick={saveProfile}
            disabled={savingProfile}
            className="px-4 py-2 rounded bg-brand-red hover:bg-red-700 text-sm font-medium disabled:opacity-50"
          >
            {savingProfile ? t("common.saving") : t("common.save")}
          </button>
          {profileMsg && <span className="text-sm text-brand-muted">{profileMsg}</span>}
        </div>
      </section>

      {/* Preferences: language + theme */}
      <section className="p-5 rounded-xl bg-brand-surface border border-brand-line">
        <h2 className="text-sm font-semibold mb-4">{t("profile.preferences")}</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <div>
            <label className="block text-sm text-brand-muted mb-2">{t("profile.language")}</label>
            <Select
              value={locale}
              options={LOCALES.map((l) => ({ value: l, label: t(`language.${l}`) }))}
              onChange={(v) => onLocaleChange(v as Locale)}
              className="w-full"
            />
          </div>
          <div>
            <label className="block text-sm text-brand-muted mb-2">{t("profile.theme")}</label>
            <Select
              value={pref}
              options={THEME_PREFS.map((p) => ({ value: p, label: t(`theme.${p}`) }))}
              onChange={(v) => onThemeChange(v as ThemePref)}
              className="w-full"
            />
          </div>
        </div>
      </section>

      {/* Change password */}
      <section className="p-5 rounded-xl bg-brand-surface border border-brand-line">
        <h2 className="text-sm font-semibold mb-4">{t("profile.changePassword")}</h2>
        <form onSubmit={changePassword} className="space-y-3">
          <label className="block">
            <span className="text-sm text-brand-muted">{t("profile.currentPassword")}</span>
            <input
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              className="mt-1 w-full px-3 py-2 rounded bg-brand-dark border border-brand-line focus:border-brand-red outline-none"
              required
            />
          </label>
          <label className="block">
            <span className="text-sm text-brand-muted">{t("profile.newPassword")}</span>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="mt-1 w-full px-3 py-2 rounded bg-brand-dark border border-brand-line focus:border-brand-red outline-none"
            />
          </label>
          <label className="block">
            <span className="text-sm text-brand-muted">{t("profile.confirmPassword")}</span>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="mt-1 w-full px-3 py-2 rounded bg-brand-dark border border-brand-line focus:border-brand-red outline-none"
            />
          </label>
          {passwordMsg && (
            <p className={`text-sm ${passwordMsg.ok ? "text-green-400" : "text-red-400"}`}>
              {passwordMsg.text}
            </p>
          )}
          <button
            type="submit"
            disabled={savingPassword}
            className="px-4 py-2 rounded bg-brand-red hover:bg-red-700 text-sm font-medium disabled:opacity-50"
          >
            {savingPassword ? t("common.saving") : t("common.save")}
          </button>
        </form>
      </section>
    </div>
  );
}
